import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "modal",
    "title",
    "fieldsList",
    "initialMessage",
    "editForm",
    "searchInput",
    "valuesList",
    "addNewSection",
    "submitButton"
  ]

  static values = {
    fieldName: String,
    fieldType: String,
    existingValues: Array,
    selectedValue: String,
    translations: Object,
    csrfToken: String,
    maxSelection: Number,
    vatClassifications: Array,
    lookups: Array,
    lookupType: String,
    syncedTypes: Array
  }

  getFieldTranslation(fieldName) {
    return this.translationsValue[fieldName] || fieldName
  }

  connect() {
    this.element.addEventListener('modal:open', this.handleCustomOpen.bind(this))
    this.csrfTokenValue = document.querySelector('meta[name="csrf-token"]')?.content || ''

    this.handleFormSubmit = this.handleFormSubmit.bind(this)
    this.editFormTarget.addEventListener('submit', this.handleFormSubmit)

    // Listen for lookup creation events from the shared modal
    this.handleLookupCreated = this.handleLookupCreated.bind(this)
    document.addEventListener('lookup-created', this.handleLookupCreated)

    // Generate unique modal ID for this instance
    this.modalId = `batch-edit-${Date.now()}`
  }

  disconnect() {
    this.element.removeEventListener('modal:open', this.handleCustomOpen.bind(this))
    this.editFormTarget.removeEventListener('submit', this.handleFormSubmit)
    document.removeEventListener('lookup-created', this.handleLookupCreated)
  }

  handleLookupCreated(event) {
    const { lookup, lookupType, callerId } = event.detail
    // Only handle events meant for this modal instance
    if (callerId !== this.modalId) return

    // Add the new lookup to our list
    this.lookupsValue = [...this.lookupsValue, lookup]

    // Select the new lookup
    this.selectedLookupId = lookup.id
    this.selectedValueValue = lookup.kodas
    this.searchInputTarget.value = lookup.kodas

    // Update the list to show the new lookup
    this.updateValuesList()
  }

  getSelectedCount() {
    return document.querySelectorAll('[data-invoice-selection-target="rowCheckbox"]:checked').length
  }

  handleCustomOpen(event) {
    const { title, fields, translations } = event.detail
    const selectedCount = this.getSelectedCount()
    const tooManySelected = selectedCount > this.maxSelectionValue
    const urlParams = new URLSearchParams(window.location.search)
    const hasCompanyId = urlParams.get('company_id')
    // Company-centric UX: perspective derived from company_id
    const hasPerspectiveContext = !!hasCompanyId

    this.titleTarget.textContent = title
    this.translationsValue = translations

    // Add VAT classification field
    const vatClassifications = this.hasVatClassificationsValue ? this.vatClassificationsValue : []
    const allFields = [
      ...fields,
      {
        field_name: 'vat_classification',
        target_model: 'invoice_vat_line',
        existing_values: vatClassifications.map(vc => ({
          display: `${vc.code} - ${vc.name}`,
          id: vc.id
        })),
        disabled: false
      }
    ]

    this.fieldsListTarget.innerHTML = `
      ${tooManySelected ? `
        <div class="p-4 text-sm text-red-600">
          Pasirinkta per daug sąskaitų (${selectedCount}). Galima pasirinkti ne daugiau kaip ${this.maxSelectionValue} sąskaitų.
        </div>
      ` : `
        <div class="p-2 space-y-1">
          ${allFields.map(field => {
            // VAT classification doesn't require perspective context
            const needsPerspective = field.field_name !== 'vat_classification' && !hasPerspectiveContext
            const isDisabled = needsPerspective || field.disabled || tooManySelected
            const baseClasses = 'w-full text-left px-3 py-2 text-sm rounded-md hover:bg-gray-50 flex items-center border-l-4 border-transparent'
            const cursorClass = isDisabled ? 'opacity-50 cursor-not-allowed hover:bg-white' : 'cursor-pointer'
            return `
              <button class="${baseClasses} ${cursorClass}"
                      data-action="click->batch-edit-modal#openFieldEdit"
                      data-field-name="${field.field_name}"
                      data-field-type="${field.target_model === 'invoice_vat_line' ? 'vat_line' : 'invoice'}"
                      data-existing-values='${JSON.stringify(field.existing_values || [])}'
                      ${isDisabled ? 'disabled' : ''}>
                <div class="flex-1">
                  <div class="flex items-center justify-between">
                    <span>${this.getFieldTranslation(field.field_name)}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"
                         class="w-4 h-4 text-gray-400 group-hover:text-gray-500">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </div>
                  ${needsPerspective ? `
                    <div class="text-xs text-gray-500 mt-0.5">
                      Pasirinkite įmonę filtruose
                    </div>
                  ` : ''}
                </div>
              </button>
            `
          }).join('')}
        </div>
      `}
    `

    this.initialMessageTarget.classList.remove('hidden')
    this.editFormTarget.classList.add('hidden')
    this._showModal()

    // Auto-select field if only one available or all available
    if (!tooManySelected) {
      setTimeout(() => {
        const enabledButtons = this.fieldsListTarget.querySelectorAll('button:not([disabled])')
        if (enabledButtons.length === 1 || enabledButtons.length === allFields.length) {
          enabledButtons[0].click()
        }
      }, 0)
    }
  }

  _showModal() {
    this.modalTarget.showModal()
  }

  close() {
    this.modalTarget.close()
  }

  closed() {
    this.initialMessageTarget.classList.remove('hidden')
    this.editFormTarget.classList.add('hidden')
    this.searchInputTarget.value = ''
    this.selectedValueValue = ''
  }

  closeOnBackdrop(event) {
    if (event.target === this.modalTarget) this.close()
  }

  search(event) {
    const searchTerm = event.target.value.toLowerCase()
    this.updateValuesList(searchTerm)
  }

  updateValuesList(searchTerm = '') {
    this.valuesListTarget.innerHTML = ''

    // If using lookups, show lookup options
    if (this.lookupTypeValue && this.lookupsValue.length > 0) {
      const filteredLookups = this.lookupsValue.filter(lookup => {
        const kodas = (lookup.kodas || '').toLowerCase()
        const pavadinimas = (lookup.pavadinimas || '').toLowerCase()
        return kodas.includes(searchTerm.toLowerCase()) || pavadinimas.includes(searchTerm.toLowerCase())
      })

      filteredLookups.forEach(lookup => {
        const button = this.createLookupButton(lookup)
        this.valuesListTarget.appendChild(button)
      })

      // Show "Sukurti naują" unless type is synced
      const isSynced = this.syncedTypesValue.includes(this.lookupTypeValue)
      this.addNewSectionTarget.classList.toggle('hidden', isSynced)
    } else if (!this.lookupTypeValue) {
      // Legacy: use existing values (for vat_classification and non-lookup fields)
      const filteredValues = this.existingValuesValue.filter(value => {
        const displayText = typeof value === 'object' ? value.display : value
        return displayText.toLowerCase().includes(searchTerm.toLowerCase())
      })

      filteredValues.forEach(value => {
        const displayText = typeof value === 'object' ? value.display : value
        const valueId = typeof value === 'object' ? value.id : null

        const button = document.createElement('button')
        button.type = 'button'
        const baseClasses = 'w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded-md cursor-pointer'
        const selectedClass = displayText === this.selectedValueValue ? ' bg-gray-50 font-medium' : ''
        button.className = baseClasses + selectedClass
        button.textContent = displayText
        button.dataset.value = displayText
        if (valueId) button.dataset.vatClassificationId = valueId
        button.dataset.action = 'click->batch-edit-modal#selectValue'
        this.valuesListTarget.appendChild(button)
      })

      const isVatClassification = this.fieldNameValue === 'vat_classification'
      const exactMatch = this.existingValuesValue.some(value => {
        const displayText = typeof value === 'object' ? value.display : value
        return displayText.toLowerCase() === searchTerm.toLowerCase()
      })
      this.addNewSectionTarget.classList.toggle('hidden', isVatClassification || exactMatch || !searchTerm)
    } else {
      // Using lookups but none loaded yet - show add new unless synced type
      const isSynced = this.syncedTypesValue.includes(this.lookupTypeValue)
      this.addNewSectionTarget.classList.toggle('hidden', isSynced)
    }
  }

  createLookupButton(lookup) {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded-md flex items-center justify-between cursor-pointer'
    button.dataset.action = 'click->batch-edit-modal#selectLookup'
    button.dataset.lookupId = lookup.id
    button.dataset.kodas = lookup.kodas
    button.dataset.pavadinimas = lookup.pavadinimas || ''

    let displayHtml = `<div class="flex flex-col min-w-0"><span class="font-medium truncate">${this.escapeHtml(lookup.kodas)}</span>`
    if (lookup.pavadinimas) {
      displayHtml += `<span class="text-xs text-gray-500 truncate">${this.escapeHtml(lookup.pavadinimas)}</span>`
    }
    displayHtml += '</div>'
    button.innerHTML = displayHtml

    return button
  }

  selectLookup(event) {
    const lookupId = event.currentTarget.dataset.lookupId
    const kodas = event.currentTarget.dataset.kodas
    this.selectedValueValue = kodas
    this.selectedLookupId = lookupId
    this.searchInputTarget.value = kodas
    this.updateValuesList()
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  selectValue(event) {
    const value = event.currentTarget.dataset.value
    this.selectedValueValue = value
    this.searchInputTarget.value = value
    this.updateValuesList()
  }

  openFieldEdit(event) {
    const button = event.currentTarget
    const fieldName = button.dataset.fieldName
    const fieldType = button.dataset.fieldType
    const existingValues = JSON.parse(button.dataset.existingValues || '[]')

    // fieldName is the lookup type directly (e.g., 'preke', 'sandelis')
    const knownLookupTypes = ['preke', 'padalinys', 'objektas', 'serija', 'centras', 'atskaitingas_asmuo', 'sandelis', 'korespondencija', 'projektas']
    this.lookupTypeValue = knownLookupTypes.includes(fieldName) ? fieldName : ''

    this.fieldsListTarget.querySelectorAll('button').forEach(btn => {
      btn.classList.toggle('bg-gray-50', btn === button)
      btn.classList.toggle('text-primary', btn === button)
      btn.classList.toggle('font-medium', btn === button)
      btn.classList.toggle('border-l-4', btn === button)
      btn.classList.toggle('border-primary', btn === button)
    })

    this.initialMessageTarget.classList.add('hidden')
    this.editFormTarget.classList.remove('hidden')

    this.fieldNameValue = fieldName
    this.fieldTypeValue = fieldType
    this.existingValuesValue = existingValues
    this.selectedValueValue = ''

    this.submitButtonTarget.setAttribute('type', 'submit')

    // Load lookups if this field uses them
    if (this.lookupTypeValue) {
      this.loadLookups(this.lookupTypeValue)
    } else {
      this.lookupsValue = []
    }

    this.updateValuesList()
  }

  async loadLookups(lookupType) {
    try {
      const response = await fetch(`/accounting_lookups.json?lookup_type=${lookupType}`)
      if (response.ok) {
        const data = await response.json()
        this.lookupsValue = data.lookups || []
        this.updateValuesList()
      }
    } catch (error) {
      console.error('Error loading lookups:', error)
      this.lookupsValue = []
    }
  }

  async handleFormSubmit(event) {
    event.preventDefault()
    event.stopPropagation()

    const value = this.searchInputTarget.value.trim()
    if (!value) return

    // Validate perspective context (company_id) before bulk editing
    const urlParams = new URLSearchParams(window.location.search)
    const hasCompanyId = urlParams.get('company_id')
    if (!hasCompanyId && this.fieldNameValue !== 'vat_classification') {
      throw new Error('Company must be selected before bulk editing')
    }

    // Capture current selection state before any async operations
    // This ensures we use the exact selection state at form submission time,
    // preventing timing issues where DOM state might change during async operations
    const currentSelectedInvoiceIds = this.getSelectedInvoiceIds()

    // Check if this is a new value that needs to be saved
    const isNewValue = !this.existingValuesValue.includes(value)

    try {
      // Auto-save new custom field values when user presses Enter
      // This ensures new values are available in future batch edit sessions
      // Don't save for vat_classification or lookup types (they use accounting_lookups table)
      if (isNewValue && this.fieldNameValue !== 'vat_classification' && !this.lookupTypeValue) {
        await this.saveNewValue(value)
      }
      
      // Build update list from captured selection state instead of re-querying DOM
      // This prevents inconsistencies where selection state might change between capture and use
      const selectedIds = currentSelectedInvoiceIds.flatMap(invoiceId => {
        // For each selected invoice ID, find its rows to handle VAT lines properly
        const invoiceRows = document.querySelectorAll(`tr[data-invoice-id="${invoiceId}"]`)
        
        return Array.from(invoiceRows).flatMap(tr => {
          // If this is a VAT line row, return its specific VAT line ID
          if (tr.dataset.vatLineId) {
            return [{ invoiceId, vatLineId: tr.dataset.vatLineId }]
          }
          
          // If this is an invoice row, get all its VAT lines
          const vatLineRows = tr.parentElement.querySelectorAll(`[data-vat-line-id][data-invoice-id="${invoiceId}"]`)
          if (vatLineRows.length > 0) {
            return Array.from(vatLineRows).map(vatLineRow => ({
              invoiceId,
              vatLineId: vatLineRow.dataset.vatLineId
            }))
          }
          
          // If no VAT lines found, return just the invoice ID
          return [{ invoiceId }]
        })
      })

      // Remove duplicates by creating a unique key for each combination
      const uniqueIds = Array.from(new Set(
        selectedIds.map(id => JSON.stringify({
          invoiceId: id.invoiceId,
          vatLineId: id.vatLineId
        }))
      )).map(key => JSON.parse(key))

      if (!uniqueIds.length) return

      // Both invoice_ids (for updates) and selected_invoice_ids (for UI state)
      // are now based on the same captured selection state for consistency

      if (uniqueIds.length > this.maxSelectionValue) {
        alert(`Galite pasirinkti ne daugiau kaip ${this.maxSelectionValue} sąskaitų masiniam atnaujinimui.`)
        return
      }

      const headers = {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfTokenValue,
        "Accept": "text/vnd.turbo-stream.html"
      }
      const filterParams = Object.fromEntries(new URLSearchParams(window.location.search))

      // Get the ID to send to the server
      let finalValue = value
      if (this.fieldNameValue === 'vat_classification') {
        // For vat_classification, get ID from selected button's data attribute
        const selectedButton = Array.from(this.valuesListTarget.querySelectorAll('button'))
          .find(btn => btn.dataset.value === value)
        finalValue = selectedButton?.dataset.vatClassificationId || value
      } else if (this.lookupTypeValue && this.selectedLookupId) {
        // For lookup fields, use the stored lookup ID (not the kodas text)
        finalValue = this.selectedLookupId
      }

      if (this.fieldTypeValue === 'vat_line') {
        const response = await fetch('/invoices/bulk_update_custom_field', {
          method: "PATCH",
          headers,
          body: JSON.stringify({
            invoice_ids: uniqueIds.map(ids => ids.invoiceId),
            vat_line_ids: uniqueIds.map(ids => ids.vatLineId),
            selected_invoice_ids: currentSelectedInvoiceIds,
            field_name: this.fieldNameValue,
            field_type: this.fieldTypeValue,
            value: finalValue,
            company_id: filterParams.company_id,
            filter_params: filterParams
          })
        })

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to save values: ${response.status}`);
        }

        const html = await response.text()
        Turbo.renderStreamMessage(html)
      } else {
        const response = await fetch('/invoices/bulk_update_custom_field', {
          method: "PATCH",
          headers,
          body: JSON.stringify({
            invoice_ids: uniqueIds.map(ids => ids.invoiceId),
            selected_invoice_ids: currentSelectedInvoiceIds,
            field_name: this.fieldNameValue,
            field_type: this.fieldTypeValue,
            value: finalValue,
            company_id: filterParams.company_id,
            filter_params: filterParams
          })
        })

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to save values: ${response.status}`);
        }

        const html = await response.text()
        Turbo.renderStreamMessage(html)
      }

      this.close()
    } catch (error) {
      console.error('Failed to save values:', error)
    }
  }

  async addNewValue() {
    const value = this.searchInputTarget.value.trim()

    // If using lookups, open the shared create modal (value can be empty for prefill)
    if (this.lookupTypeValue) {
      this.openCreateModal()
      return
    }

    // Legacy: save raw text value - requires value
    if (!value) return
    await this.saveNewValue(value)
    this.selectedValueValue = value
    this.updateValuesList()
  }

  openCreateModal() {
    // Dispatch event to open the shared lookup create modal
    document.dispatchEvent(new CustomEvent('open-lookup-create-modal', {
      detail: {
        lookupType: this.lookupTypeValue,
        prefillKodas: this.searchInputTarget.value.trim(),
        callerId: this.modalId
      }
    }))
  }

  async saveNewValue(value) {
    try {
      const response = await fetch("/tenants/add_custom_field_value", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          field_name: this.fieldNameValue,
          value: value
        })
      })

      if (!response.ok) {
        throw new Error(`Failed to add new value: ${response.status}`)
      }

      // Update local values for current field
      this.existingValuesValue = [...this.existingValuesValue, value]
      
      // Update the global existing field values data attribute so other fields can see the new value
      const invoiceSelectionElement = document.querySelector('[data-controller*="invoice-selection"]')
      if (invoiceSelectionElement) {
        const existingFieldValues = JSON.parse(invoiceSelectionElement.dataset.existingFieldValues || '{}')
        existingFieldValues[this.fieldNameValue] = existingFieldValues[this.fieldNameValue] || []
        if (!existingFieldValues[this.fieldNameValue].includes(value)) {
          existingFieldValues[this.fieldNameValue].push(value)
        }
        invoiceSelectionElement.dataset.existingFieldValues = JSON.stringify(existingFieldValues)
      }
    } catch (error) {
      console.error('Failed to add new value:', error)
      throw error // Re-throw so the caller can handle it
    }
  }

  // Get currently selected invoice IDs from the UI
  // NOTE: This method handles both desktop (table row) and mobile (card) checkboxes
  // that may exist for the same invoice, ensuring we get a clean deduplicated list
  getSelectedInvoiceIds() {
    const selectedCheckboxes = document.querySelectorAll('[data-invoice-selection-target="rowCheckbox"]:checked')
    
    const invoiceIds = Array.from(selectedCheckboxes)
      .map(checkbox => {
        // First try table row (desktop layout)
        const row = checkbox.closest('tr')
        if (row && row.dataset.invoiceId) {
          return row.dataset.invoiceId
        }
        
        // Then try mobile card container (mobile layout)
        const card = checkbox.closest('[data-invoice-id]')
        if (card && card.dataset.invoiceId) {
          return card.dataset.invoiceId
        }
        
        return null
      })
      .filter(id => id !== null)
    
    // Remove duplicates since same invoice may have both desktop and mobile checkboxes
    // This deduplication ensures accurate selection state
    return [...new Set(invoiceIds)]
  }

  _restoreSelection(invoiceIds) {
    const controller = this.application.getControllerForElementAndIdentifier(this.element, 'invoice-selection')
    if (!controller) return

    invoiceIds.forEach(id => {
      const checkbox = document.querySelector(`tr[data-invoice-id="${id}"] [data-invoice-selection-target="rowCheckbox"]`)
      if (checkbox) {
        checkbox.checked = true
        const event = new Event('change', { bubbles: true })
        checkbox.dispatchEvent(event)
      }
    })
  }
}
